import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { MyTask } from '@app/models/my-task';
import { CacheService } from '@app/services/cache.service';
import { OutlookTask, OutlookTaskFolder } from '@microsoft/microsoft-graph-types-beta';
import { startOfToday } from 'date-fns';
import { concat, from, Observable, of, Subject } from 'rxjs';
import { catchError, map, mergeMap, shareReplay, takeUntil, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class OutlookService {

  constructor(private http: HttpClient, private cache: CacheService, private httpHelper: HttpHelper) { }

  public useCache = true;
  private readonly cacheKeyPrefix = 'OutlookService';
  private cuncurrencyValue = 4;

  private getMyOutlookTaskFolders$: Observable<Array<OutlookTaskFolder>>;

  private reloadGetOutlookTasks$ = new Subject<void>();

  reloadGetOutlookTasks() {
    this.reloadGetOutlookTasks$.next();
    this.getMyOutlookTaskFolders$ = null;
  }

  getMyOutlookTasks(top: number): Observable<Array<OutlookTask>> {

    const cacheKey = `${this.cacheKeyPrefix}-getMyOutlookTasks-${top}`;

    const fetchOperation = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_BETA_VERSION}/me/outlook/tasks?$top=${top}`)
      .pipe(
        map(response => {
          this.useCache && this.cache.set(cacheKey, response.value);
          return response.value;
        }));

    if (this.useCache) {
      const cachedData = this.cache.get<Array<OutlookTask>>(cacheKey);
      if (cachedData) {
        return concat(of(cachedData), fetchOperation);
      }
    }
    return fetchOperation;
  }

  getMyOutlookTaskFolders(): Observable<Array<OutlookTaskFolder>> {

    if (this.getMyOutlookTaskFolders$) {
      return this.getMyOutlookTaskFolders$;
    } else {
      const cacheKey = `${this.cacheKeyPrefix}-getMyOutlookTaskFolders`;

      this.getMyOutlookTaskFolders$ = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_BETA_VERSION}/me/outlook/taskFolders`)
        .pipe(
          map(response => {
            this.useCache && this.cache.set(cacheKey, response.value);
            return response.value;
          }))
        .pipe(
          catchError(this.httpHelper.handleError({ operation: 'getMyOutlookTaskFolders', result: new Array<OutlookTaskFolder>(), rethrow: true })),
          takeUntil(this.reloadGetOutlookTasks$),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<OutlookTask>>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getMyOutlookTaskFolders$);
        }
      }

      return this.getMyOutlookTaskFolders$;
    }
  }

  getMyOutlookTasksFromFoldersBatch(ids: Array<string>, top: number): Observable<Array<OutlookTask>> {

    const todayDate = startOfToday();
    const tasks = [];
    const httpParams = new HttpParams()
      .append('$filter', 'completedDateTime/dateTime ge \'' + todayDate.toISOString() + '\' or status ne \'completed\'')
      .append('$top', top.toString());

    if (ids.length === 0) {
      return of(null);
    }

    return from(ids)
      .pipe(
        mergeMap(id =>
          this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_BETA_VERSION}/me/outlook/taskFolders/${id}/tasks`,
            {
              params: httpParams
            })
            .pipe(
              map(response => {
                response.value.forEach(r => {
                  tasks.push(r);
                });

                return tasks;
              }),
              catchError(this.httpHelper.handleError({ operation: 'getMyOutlookTasksFromFoldersBatch', result: ({} as Array<OutlookTask>) }))),
          this.cuncurrencyValue));
  }

  completeOutlookTask(task: MyTask): Observable<OutlookTask> {

    return this.http.post<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_BETA_VERSION}/me/outlook/tasks/${task.Id}/complete`, {})
      .pipe(
        tap(response => {
          console.log(`completed outlook task: `);
          console.log(response);
        }),
        catchError(this.httpHelper.handleError({ operation: 'completeOutlookTask', result: {}, rethrow: true })));
  }

}
